/**
 * <privacy-notice> — first-visit privacy notice (pop-up or bar).
 * Consent state itself is handled by window.NWPrivacy
 * (snippets/privacy-consent.liquid); this element only shows the notice,
 * collects the visitor's choice and remembers that the notice was shown.
 */
if (!customElements.get('privacy-notice')) {
  class PrivacyNotice extends HTMLElement {
    constructor() {
      super();
      this.cookieName = 'nw_privacy_notice';
      this.sectionId = this.dataset.sectionId;
      this.isPopup = this.classList.contains('privacy-notice--popup');
    }

    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.dialog = this.querySelector('.privacy-notice__dialog');
      this.choices = this.querySelector('.privacy-notice__choices');
      this.btnSave = this.querySelector('[data-action="save"]');
      this.btnManage = this.querySelector('[data-action="choices"]');
      this.btnAccept = this.querySelector('[data-action="accept"]');
      this.btnDecline = this.querySelector('[data-action="decline"]');

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-action]');
        if (!button || !this.contains(button)) return;
        const handler = this['on_' + button.dataset.action];
        if (typeof handler === 'function') handler.call(this, event);
      });
      this.addEventListener('keyup', (event) => {
        if (event.code === 'Escape' && this.querySelector('.privacy-notice__close')) this.on_close();
      });

      if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.sectionId) this.show();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.sectionId) this.hide();
        });
        return;
      }

      if (this.suppressedByApp()) return;
      if (this.alreadyShown()) return;
      if (window.NWPrivacy && window.NWPrivacy.hasDecided()) {
        // Visitor already made a choice (e.g. via the consent app) – don't nag.
        this.markShown();
        return;
      }

      const delay = parseFloat(this.dataset.delay || '0') * 1000;
      window.setTimeout(() => this.show(), Math.max(0, delay));
    }

    /* ---------- state ---------- */
    suppressedByApp() {
      if (this.dataset.suppressApp !== 'true') return false;
      try {
        const rules = window.pandectesRulesSettings;
        return !!(rules && rules.banner && rules.banner.enabled);
      } catch (e) {
        return false;
      }
    }

    alreadyShown() {
      const raw = this.getCookie(this.cookieName);
      if (!raw) return false;
      const shownAt = parseInt(raw, 10);
      if (!shownAt) return false;
      const days = parseInt(this.dataset.reshowDays || '365', 10);
      return Date.now() - shownAt < days * 864e5;
    }

    markShown() {
      this.setCookie(this.cookieName, String(Date.now()), 365);
    }

    syncToggles() {
      const privacy = window.NWPrivacy;
      this.querySelectorAll('[data-consent]').forEach((input) => {
        input.checked = privacy ? privacy.allowed(input.dataset.consent) : true;
      });
    }

    /* ---------- visibility ---------- */
    show() {
      this.syncToggles();
      this.hidden = false;
      window.setTimeout(() => this.setAttribute('open', ''), 30);
      if (this.isPopup) {
        document.body.classList.add('privacy-notice-open');
        window.setTimeout(() => this.dialog && this.dialog.focus(), 60);
      }
      // Opt-out ("implied") mode: the visitor has now been notified; continuing
      // to browse counts as consent, so don't repeat the notice on every page.
      if (window.NWPrivacy && window.NWPrivacy.mode === 'implied') this.markShown();
    }

    hide() {
      this.removeAttribute('open');
      document.body.classList.remove('privacy-notice-open');
      window.setTimeout(() => {
        this.hidden = true;
        this.closeChoices();
      }, 320);
    }

    openChoices() {
      // Opened on demand (footer links) – works even when the first-visit
      // pop-up is suppressed in favour of the consent app banner.
      if (this.hidden || !this.hasAttribute('open')) this.show();
      if (this.choices && this.choices.hidden) this.toggleChoices(true);
    }

    closeChoices() {
      this.toggleChoices(false);
    }

    toggleChoices(open) {
      if (!this.choices) return;
      this.choices.hidden = !open;
      this.classList.toggle('privacy-notice--choices-open', open);
      if (this.btnManage) this.btnManage.setAttribute('aria-expanded', String(open));
      if (this.btnSave) this.btnSave.hidden = !open;
      if (this.btnAccept) this.btnAccept.hidden = open;
      if (this.btnDecline) this.btnDecline.hidden = open;
      if (open) this.syncToggles();
    }

    /* ---------- actions ---------- */
    on_accept() {
      if (window.NWPrivacy) window.NWPrivacy.setConsent({ analytics: true, marketing: true, preferences: true });
      this.markShown();
      this.hide();
    }

    on_decline() {
      if (window.NWPrivacy) window.NWPrivacy.setConsent({ analytics: false, marketing: false, preferences: false });
      this.markShown();
      this.hide();
    }

    on_save() {
      const consent = { preferences: true };
      this.querySelectorAll('[data-consent]').forEach((input) => {
        consent[input.dataset.consent] = input.checked;
      });
      if (window.NWPrivacy) window.NWPrivacy.setConsent(consent);
      this.markShown();
      this.hide();
    }

    on_choices() {
      if (this.dataset.appChoices === 'true' && window.Pandectes && typeof window.Pandectes.openDialog === 'function') {
        try {
          window.Pandectes.openDialog('preferences');
          this.markShown();
          this.hide();
          return;
        } catch (e) {
          /* fall through to the built-in panel */
        }
      }
      this.toggleChoices(this.choices ? this.choices.hidden : true);
    }

    on_close() {
      this.markShown();
      this.hide();
    }

    /* ---------- cookies ---------- */
    getCookie(name) {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    }

    setCookie(name, value, days) {
      const expires = new Date(Date.now() + days * 864e5).toUTCString();
      document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
    }
  }

  customElements.define('privacy-notice', PrivacyNotice);
}
